import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiSend } from '../lib/api'
import type { KimItem, Me, SectionItem, Task, Tree } from '../lib/types'

type Axis = 'tree' | 'kim' | 'sections'

/**
 * Главный экран — веб-аналог бот-команды /start: то же дерево тем, что и там,
 * прямо на экране, без отдельной вкладки «Каталог» и без промежуточной кнопки
 * «Выбрать тему самому» (в боте дерево — это и есть меню, а не ссылка на него).
 *
 * У ОГЭ дерево разложено по темам физики — не хватает оси заданий; у ЕГЭ
 * наоборот. Ось не заменяет список, а добавляется к нему поверх родного —
 * так обе ветки устроены одинаково, как и в build_nav_menu бота.
 */
export default function Catalog({ me }: { me: Me }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Узел и модуль — в адресе (?node=…&m=…), а не в состоянии экрана: прежде адрес
  // всегда оставался «/», и обновление страницы или «назад» браузера выбрасывали
  // ученика из темы в главное меню (M3-WEB-18).
  const [params, setParams] = useSearchParams()
  const node = params.get('node')
  const module = params.get('m')
  function перейти(n: string | null, m: string | null) {
    const p = new URLSearchParams()
    if (n) p.set('node', n)
    if (m) p.set('m', m)
    setParams(p)
  }
  const [axis, setAxis] = useState<Axis>('tree')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const isRoot = node === null

  const { data: tree, isLoading } = useQuery<Tree>({
    queryKey: ['tree', node, module],
    queryFn: () =>
      apiGet(
        `/api/tree?${new URLSearchParams({
          ...(node ? { node } : {}),
          ...(module ? { module } : {}),
        })}`,
      ),
  })

  const { data: current, isLoading: currentLoading } = useQuery<{ task: Task | null }>({
    queryKey: ['task', 'current'],
    queryFn: () => apiGet('/api/tasks/current'),
    enabled: isRoot,
  })

  // Заход в дерево экзамена выбирает экзамен (сервер запоминает его сам, как бот,
  // правило §2.14a). Профиль и цель в браузере закэшированы — без обновления
  // «Цель и прогресс» продолжала бы показывать прежний экзамен.
  const экзаменДерева = tree?.exam_type ?? null
  useEffect(() => {
    if (!экзаменДерева || экзаменДерева === me.exam.code) return
    if (!me.exams.some((e) => e.code === экзаменДерева)) return
    queryClient.invalidateQueries({ queryKey: ['me'] })
    queryClient.invalidateQueries({ queryKey: ['progress'] })
  }, [экзаменДерева, me.exam.code, me.exams, queryClient])

  const активная = isRoot && current?.task && !current.task.solved ? current.task : null

  async function serve(body: Record<string, unknown>, replace = false) {
    setBusy(true)
    setError('')
    try {
      const task = await apiSend<Task>('/api/tasks/next', 'POST', body)
      queryClient.setQueryData(['task', 'current'], { task })
      navigate('/task', { replace })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не получилось взять задачу.')
    } finally {
      setBusy(false)
    }
  }

  function open(child: Tree['children'][number]) {
    if (child.empty) return
    setAxis('tree')
    перейти(child.id, child.module === 'theory' ? 't' : child.module === 'practice' ? 'p' : module)
  }

  function назад() {
    if (axis !== 'tree') return setAxis('tree')
    перейти(tree?.node?.parent_id ?? null, module)
  }

  // От локального node отличается на один шаг: клик «Назад» со свёрнутого
  // уровня предмета сервер сам разворачивает обратно в корень (tree.py), и
  // отрисовка должна следовать за тем, что реально пришло, а не за тем, что
  // мы запрашивали.
  const apiRoot = tree ? tree.node === null : isRoot
  const листТеории = !!tree && tree.is_leaf && module === 't'
  const листЗадач = !!tree && tree.is_leaf && module !== 't'

  // Лист теории объясняется сразу, как в боте (правило §2.25 CLAUDE.md) — без
  // промежуточной кнопки «Объясни тему»: ученик уже выбрал тему, второй клик
  // по тому же намерению ничего не решает, только откладывает ответ.
  //
  // Хук обязан вызываться на КАЖДОМ рендере в одном и том же порядке (иначе
  // «Rendered more hooks than during the previous render») — поэтому он стоит
  // ДО проверки isLoading, а не после неё.
  useEffect(() => {
    if (листТеории && tree?.node) navigate(`/theory/${tree.node.id}`, { replace: true })
  }, [листТеории, tree?.node, navigate])

  // Лист практики (и задание из одной темы) — задача сразу, как в боте
  // (serve_target в handlers/nav_menu.py): экран с одной кнопкой «Решить задачу»
  // лишь повторял то, зачем ученик сюда шёл. Адрес темы заменяется адресом
  // задачи, иначе «назад» из задачи снова выдавал бы новую. Тема без задач
  // остаётся на экране с ошибкой — повторно её не дёргаем.
  const выдано = useRef<string | null>(null)
  const сразу = tree?.serve_node ?? null
  useEffect(() => {
    if (!сразу || выдано.current === сразу) return
    выдано.current = сразу
    serve({ mode: 'topic', node_id: сразу }, true)
  }, [сразу]) // serve пересоздаётся на каждом рендере — зависим только от темы


  // Главная ждёт и незаконченную задачу: строка «↩️ Вернуться к задаче» иначе
  // появлялась позже меню и сдвигала его — клик в первые секунды попадал мимо
  // «Решение задач» (живой прогон 10 октября 2026).
  if (isLoading || !tree || (isRoot && currentLoading)) {
    return (
      <>
        {/* «Каталог» мелькал при каждом переходе по дереву — такого слова в
            интерфейсе больше нет (живой прогон 25 октября 2026). */}
        <h1>{isRoot ? 'Главное меню' : module === 't' ? 'Теория' : 'Решение задач'}</h1>
        <div className="skeleton" />
        <div className="skeleton" />
        <div className="skeleton" />
      </>
    )
  }

  if (листТеории) return null

  return (
    <>
      {/* Корень называется по-бото́вски («📍 Главное меню» уже приходит в
          breadcrumbs с сервера); у раздела/темы — своё название узла, а
          крошки — отдельной строкой под ним, как и было до слияния экранов.
          У breadcrumbs пусты для module_root («Решение задач») — узел сам по
          себе и есть путь, показывать нечего. */}
      <h1>{apiRoot ? tree.breadcrumbs : tree.node?.title ?? tree.breadcrumbs}</h1>
      {!apiRoot && tree.breadcrumbs && <div className="crumbs">{tree.breadcrumbs}</div>}
      {/* Та же строка, что у бота над кнопками (menu_prompt). */}
      {!apiRoot && tree.prompt && !tree.serve_node && (
        <p className="muted" style={{ margin: '0 0 12px' }}>{tree.prompt}</p>
      )}

      {/* Главное меню бота проговаривает, откуда начать — то же самое здесь,
          дословно, вплоть до фразы про чат: она честна и на этом экране —
          свободный вопрос можно написать внутри любой открытой задачи. */}
      {apiRoot && (
        <p className="muted" style={{ margin: '0 0 16px' }}>
          Привет! Я — ИИ-репетитор по физике. Готового ответа не дам — доведу до
          него наводящими вопросами.
          <br />
          <br />
          <b>📚 Теория</b> — объясню тему простыми словами, с формулами и примерами.
          <br />
          <b>✏️ Решение задач</b> — выбери экзамен, раздел и тему: дам задачу
          и проверю решение.
          <br />
          <b>🎯 Цель и прогресс</b> — поставь цель и следи, на сколько сдаёшь сейчас.
          <br />
          <b>❓ Помощь</b> — как проходит занятие и что я ещё умею.
        </p>
      )}

      {/* Прерванная работа — первой строкой меню, как у бота (_с_возвратом_к_задаче). */}
      {активная && (
        <div className="list" style={{ marginBottom: 8 }}>
          <button className="list-item" onClick={() => navigate('/task')}>
            <span className="title">↩️ Вернуться к задаче</span>
          </button>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {tree.serve_node && busy && <div className="muted">Ищу задачу…</div>}

      {листЗадач && !tree.serve_node && (
        <button
          className="btn btn-primary btn-block btn-lg"
          disabled={busy}
          onClick={() => serve({ mode: 'topic', node_id: tree.node!.id })}
        >
          {busy ? 'Ищу задачу…' : '📝 Решить задачу'}
        </button>
      )}


      {axis === 'tree' && (tree.axes.kim || tree.axes.sections) && (
        <div className="row-wrap" style={{ marginBottom: 12 }}>
          {tree.axes.kim && (
            <button className="btn" onClick={() => setAxis('kim')}>🎯 По заданиям экзамена</button>
          )}
          {tree.axes.sections && (
            <button className="btn" onClick={() => setAxis('sections')}>📚 По разделам физики</button>
          )}
        </div>
      )}

      {/* Темы — в один столбец: они идут по порядку курса (1-й закон → масса
          → сила → 2-й закон), а в две колонки глаз читал их столбцами, и
          порядок терялся (живой прогон 22 октября 2026). */}
      {axis === 'tree' && (
        <div className="list">
          {tree.children.map((child) => (
            <button
              key={child.id}
              className="list-item"
              disabled={child.empty}
              onClick={() => open(child)}
            >
              {child.mastery_icon && <span aria-hidden>{child.mastery_icon}</span>}
              {/* Тема теории — сразу объяснение: значок книги. Раздел — «›»:
                  откроется список. Прежде оба выглядели одинаково. */}
              {!child.has_children && child.module === 'theory' && <span aria-hidden>📖</span>}
              <span className="title">{child.short_title}</span>
              {child.empty ? (
                <span className="meta">скоро</span>
              ) : (
                child.mastery_pct !== null && <span className="meta">{child.mastery_pct}%</span>
              )}
              {child.has_children && !child.empty && <span className="meta" aria-hidden>›</span>}
            </button>
          ))}
        </div>
      )}

      {/* Экзамен оси — тот, в чьём дереве ученик стоит, а не прежний из профиля. */}
      {axis === 'kim' && <KimAxis exam={экзаменДерева ?? me.exam.code} busy={busy} onPick={serve} />}
      {axis === 'sections' && (
        <SectionAxis exam={экзаменДерева ?? me.exam.code} busy={busy} onPick={serve} />
      )}

      {/* Навигационный ряд — НИЖЕ содержимого экрана, как у бота: там back_row
          тоже добавляется последней строкой клавиатуры (keyboard.append), а не
          первой. Тот же list-item, что у тем/разделов выше — одна длина. */}
      {!apiRoot && (
        <div className="list list-grid" style={{ marginTop: 8 }}>
          {(axis !== 'tree' || tree.show_back) && (
            <button className="list-item" onClick={назад}>
              <span className="title">⬅️ Назад</span>
            </button>
          )}
          <button
            className="list-item"
            onClick={() => {
              setAxis('tree')
              перейти(null, null)
            }}
          >
            <span className="title">🏠 Главная</span>
          </button>
        </div>
      )}

      {/* Тот же нижний ряд, что у главного меню бота: цель/прогресс и помощь
          рядом, отдельных кнопок под них на главной больше нет. Тот же класс
          list-item, что у «Теория»/«Решение задач» выше — это одна и та же
          строка меню, а не отдельная мелкая панель. */}
      {apiRoot && (
        <div className="list list-grid" style={{ marginTop: 8 }}>
          <button className="list-item" onClick={() => navigate('/progress')}>
            <span className="title">🎯 Цель и прогресс</span>
          </button>
          <button className="list-item" onClick={() => navigate('/help')}>
            <span className="title">❓ Помощь</span>
          </button>
          {/* Лаборатория — исключение из режима показа (§2.16), флагом не гейтится. */}
          <button className="list-item" onClick={() => navigate('/lab')}>
            <span className="title">🧪 Лаборатория</span>
          </button>
        </div>
      )}
    </>
  )
}

function KimAxis({
  exam,
  busy,
  onPick,
}: {
  exam: string
  busy: boolean
  onPick: (body: Record<string, unknown>) => void
}) {
  const { data } = useQuery<{ items: KimItem[] }>({
    queryKey: ['kim', exam],
    queryFn: () => apiGet(`/api/kim?exam=${encodeURIComponent(exam)}`),
  })
  if (!data) return <div className="skeleton" />

  return (
    <div className="list list-grid">
      {data.items.map((item) => (
        <button
          key={item.position}
          className="list-item"
          disabled={busy}
          onClick={() => onPick({ mode: 'kim', kim_positions: [item.position] })}
        >
          <span className="title">
            {item.position}. {item.title}
          </span>
          {/* Вес обязателен: у ОГЭ краткий тип повторяется, и без баллов пять
              кнопок подряд выглядели бы одинаково. */}
          <span className="meta">{item.points} б</span>
        </button>
      ))}
    </div>
  )
}

/** «задания 1–6» для сплошного ряда, иначе «задания 11, 13, 14». */
function номераЗаданий(позиции: number[]): string {
  if (позиции.length === 1) return `задание ${позиции[0]}`
  const сплошь = позиции.every((п, i) => i === 0 || п === позиции[i - 1] + 1)
  return сплошь
    ? `задания ${позиции[0]}–${позиции[позиции.length - 1]}`
    : `задания ${позиции.join(', ')}`
}

function SectionAxis({
  exam,
  busy,
  onPick,
}: {
  exam: string
  busy: boolean
  onPick: (body: Record<string, unknown>) => void
}) {
  const { data } = useQuery<{ items: SectionItem[] }>({
    queryKey: ['sections', exam],
    queryFn: () => apiGet(`/api/sections?exam=${encodeURIComponent(exam)}`),
  })
  if (!data) return <div className="skeleton" />

  return (
    <>
      <p className="muted" style={{ margin: '0 0 8px' }}>
        Нажми раздел — дам задачу из любого его задания.
      </p>
      <div className="list">
        {data.items.map((item) => (
          <button
            key={item.title}
            className="list-item"
            disabled={busy}
            onClick={() => onPick({ mode: 'kim', kim_positions: item.positions })}
          >
            <span className="title">{item.title}</span>
            <span className="meta">
              {номераЗаданий(item.positions)} · {item.points} перв. б
            </span>
          </button>
        ))}
      </div>
    </>
  )
}
